(ns hai.journal2.page
  "Integration page — composes the filter panel, modals, and entry view."
  (:require
   [hai.journal2.collection :as collection]
   [hai.journal2.events]
   [hai.journal2.timeline :as timeline]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Page styles
;; ---------------------------------------------------------------------------

(o/defstyled no-journal-message :div
  :p-8 :text-center
  {:color t/--text-secondary-50
   :font-size "1rem"})

(o/defstyled empty-state-container :div
  :flex :flex-col :items-center :justify-center :py-16 :gap-6)

(o/defstyled empty-state-heading :h2
  :text-center
  {:color t/--text-secondary-50
   :font-size "1.25rem"
   :font-weight "500"})

(o/defstyled connect-button-row :div
  :flex :flex-col :w-full :gap-3
  {:max-width "24rem"})

(o/defstyled page-container :div
  :flex :flex-col
  {:gap "0.5rem"})

(o/defstyled end-of-timeline-message :div
  :py-6 :text-center
  {:color c/--gray-9
   :font-size "0.875rem"})

;; ---------------------------------------------------------------------------
;; Infinite scroll sentinel
;; ---------------------------------------------------------------------------

(defn load-more-sentinel []
  (r/with-let [!observer (atom nil)
               ref-fn (fn [node]
                        (when-let [old @!observer] (.disconnect old))
                        (when node
                          (let [obs (js/IntersectionObserver.
                                     (fn [entries]
                                       (when (.-isIntersecting (aget entries 0))
                                         (>e [:timeline/load-more])))
                                     #js {:rootMargin "200px"})]
                            (.observe obs node)
                            (reset! !observer obs))))]
    [:div {:ref ref-fn :style {:height "1px"}}]
    (finally
      (when-let [obs @!observer] (.disconnect obs)))))

;; ---------------------------------------------------------------------------
;; Main page
;; ---------------------------------------------------------------------------

(defn page [_route]
  (r/with-let [!error             (<s :timeline/error?)
               !days              (<s :timeline/entries-by-day)
               !busy              (<s :timeline/busy?)
               !all-loaded?       (<s :timeline/all-loaded?)
               !pagination-active? (<s :timeline/pagination-active?)
               !cursor            (<s :timeline/load-more-cursor)]
    [layout/page
     [layout/breadcrumbs]
     [layout/heading {:label "Progress timeline"}
      [:div {:style {:margin-left "auto"}}
       [c/Button {:variant "soft" :size "2" :radius "full"
                  :on-click #(>e [:timeline/open-integrations-modal])}
        "Manage Integrations"]]]
     [timeline/not-connected-modal]
     [timeline/calendar-picker-modal]
     [timeline/github-username-modal]
     [timeline/add-to-contacts-modal]
     [timeline/backfill-modal]
     [collection/integrations-modal]
     ;; Filter bar is always visible — including the empty/"getting started"
     ;; state — so filters stay reachable regardless of whether entries exist.
     [timeline/date-range-filter]
     [layout/content
      (cond
        @!error
        [no-journal-message "Something went wrong loading your activity. Please try refreshing the page."]

        ;; Connect/empty state only after every load has settled and the
        ;; frontend has confirmed no entries exist — never mid-load.
        (and (empty? @!days) (not @!busy))
        [empty-state-container
         [empty-state-heading "You don't have anything here yet. Create an integration to get started."]
         [connect-button-row
          [c/Button {:size "3" :variant "solid"
                     :on-click #(>e [:timeline/add-integration-by-type :timeline-integration.type/linear])}
           [icons/linear {:width 18 :height 18 :fill "currentColor"}]
           "Connect Linear"]
          [c/Button {:size "3" :variant "solid"
                     :on-click #(>e [:timeline/open-calendar-picker])}
           [icons/calendar {:width 18 :height 18}]
           "Connect your calendar"]
          [c/Button {:size "3" :variant "solid"
                     :on-click #(>e [:timeline/add-integration-by-type :timeline-integration.type/github])}
           [:span {:style {:display "flex" :filter "brightness(0) invert(1)"}} [icons/github {:size 18}]]
           "Connect GitHub"]]]

        :else
        [page-container
         [timeline/timeline-container
          (doall
           (for [{:keys [date label entries]} @!days]
             ^{:key date}
             [timeline/collapsible-day-section
              {:label label
               :default-open? true
               :entries entries}]))]
         (when (and @!pagination-active? (not @!all-loaded?))
           ;; Remount the sentinel whenever the cursor advances so the
           ;; IntersectionObserver re-fires its initial-state callback —
           ;; otherwise a sentinel that stays in view after a short load never
           ;; triggers the next chunk.
           ^{:key (some-> @!cursor .getTime)}
           [load-more-sentinel])
         ;; Single loader for every busy state (initial load, infinite-scroll
         ;; crawl, gcal refresh, backfill). `:timeline/busy?` stays continuously
         ;; true across crawl rounds, so this is mounted once and never blinks.
         (when @!busy
           [c/Flex {:justify "center" :py "4"}
            [c/Spinner {:size "2"}]])
         (when (and @!pagination-active? @!all-loaded? (not @!busy))
           [end-of-timeline-message
            "You've reached the beginning of your activity."])])]]))
